import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Pencil,
  Trash2,
  UserCheck,
  UserX,
  Key,
  Palette,
  Globe,
  Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USER_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
  '#14B8A6', '#F43F5E', '#A855F7', '#22C55E', '#0EA5E9',
  '#D946EF', '#FCD34D', '#FB923C', '#4ADE80', '#38BDF8',
  '#C084FC', '#FB7185', '#34D399', '#FBBF24', '#818CF8'
];

const LANGUAGES = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' }
];

export default function Users() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'collaborateur',
    color: '#3B82F6',
    default_language: 'fr',
    weekly_contractual_hours: 40
  });

  const ROLE_LABELS = {
    collaborateur: { label: t('collaborator'), class: 'bg-blue-500' },
    manager: { label: t('manager'), class: 'bg-purple-500' },
    directeur: { label: t('director'), class: 'bg-amber-500' },
  };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (err) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async () => {
    try {
      await axios.post(`${API}/users`, newUser);
      toast.success(t('userCreated'));
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    try {
      await axios.put(`${API}/users/${editingUser.id}`, {
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        email: newUser.email,
        is_active: newUser.is_active,
        color: newUser.color,
        default_language: newUser.default_language,
        weekly_contractual_hours: newUser.weekly_contractual_hours
      });
      toast.success(t('userUpdated'));
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const handleDeleteUser = async (userId) => {
    const confirmMsg = language === 'de' ? 'Sind Sie sicher?' : language === 'en' ? 'Are you sure?' : 'Êtes-vous sûr ?';
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success(t('userDeleted'));
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const handleToggleActive = async (targetUser) => {
    try {
      await axios.put(`${API}/users/${targetUser.id}`, {
        is_active: !targetUser.is_active
      });
      toast.success(targetUser.is_active ? t('userDeactivated') : t('userActivated'));
      fetchUsers();
    } catch (err) {
      toast.error(t('error'));
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUserForPassword || !newPassword) return;
    try {
      await axios.put(`${API}/users/${selectedUserForPassword.id}/password?new_password=${encodeURIComponent(newPassword)}`);
      toast.success(t('passwordChanged'));
      setPasswordDialogOpen(false);
      setSelectedUserForPassword(null);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const openEditDialog = (targetUser) => {
    setEditingUser(targetUser);
    setNewUser({
      email: targetUser.email,
      password: '',
      first_name: targetUser.first_name,
      last_name: targetUser.last_name,
      role: targetUser.role,
      is_active: targetUser.is_active,
      color: targetUser.color || '#3B82F6',
      default_language: targetUser.default_language || 'fr',
      weekly_contractual_hours: targetUser.weekly_contractual_hours || 40
    });
    setDialogOpen(true);
  };

  const openPasswordDialog = (targetUser) => {
    setSelectedUserForPassword(targetUser);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setNewUser({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'collaborateur',
      color: '#3B82F6',
      default_language: 'fr',
      weekly_contractual_hours: 40
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl">{t('userManagement').toUpperCase()}</h1>
          <p className="text-muted-foreground text-sm">
            {users.length} {t('usersRegistered')}
          </p>
        </div>
        
        <Button onClick={openNewDialog} data-testid="new-user-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t('newUser')}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="stat-card">
          <p className="stat-label">{t('collaborators')}</p>
          <p className="stat-value">{users.filter(u => u.role === 'collaborateur').length}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">{t('managers')}</p>
          <p className="stat-value">{users.filter(u => u.role === 'manager').length}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">{t('active')}</p>
          <p className="stat-value">{users.filter(u => u.is_active).length}</p>
        </div>
      </div>

      {/* Users Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('userColor')}</th>
              <th>{t('name')}</th>
              <th>{t('email')}</th>
              <th>{t('role')}</th>
              <th>{t('weeklyContractualHours')}</th>
              <th>{t('defaultLanguage')}</th>
              <th>{t('status')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((targetUser) => (
              <tr key={targetUser.id} data-testid={`user-row-${targetUser.id}`}>
                <td>
                  <div 
                    className="w-6 h-6 rounded-full border-2 border-white shadow"
                    style={{ backgroundColor: targetUser.color || '#3B82F6' }}
                  />
                </td>
                <td className="font-medium">
                  {targetUser.first_name} {targetUser.last_name}
                </td>
                <td className="text-muted-foreground">{targetUser.email}</td>
                <td>
                  <Badge className={`${ROLE_LABELS[targetUser.role]?.class || 'bg-gray-500'} text-white rounded`}>
                    {ROLE_LABELS[targetUser.role]?.label || targetUser.role}
                  </Badge>
                </td>
                <td className="mono text-sm">
                  {targetUser.weekly_contractual_hours || 40}h
                </td>
                <td>
                  <span className="text-sm uppercase">{targetUser.default_language || 'fr'}</span>
                </td>
                <td>
                  {targetUser.is_active ? (
                    <span className="text-emerald-600 flex items-center gap-1">
                      <UserCheck className="h-4 w-4" /> {t('active')}
                    </span>
                  ) : (
                    <span className="text-red-500 flex items-center gap-1">
                      <UserX className="h-4 w-4" /> {t('inactive')}
                    </span>
                  )}
                </td>
                <td>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditDialog(targetUser)}
                      className="h-8 px-2"
                      data-testid={`edit-user-${targetUser.id}`}
                      title={t('edit')}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openPasswordDialog(targetUser)}
                      className="h-8 px-2"
                      data-testid={`password-user-${targetUser.id}`}
                      title={t('changePassword')}
                    >
                      <Key className="h-4 w-4 text-blue-500" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleToggleActive(targetUser)}
                      className="h-8 px-2"
                      data-testid={`toggle-user-${targetUser.id}`}
                    >
                      {targetUser.is_active ? (
                        <UserX className="h-4 w-4 text-amber-500" />
                      ) : (
                        <UserCheck className="h-4 w-4 text-emerald-500" />
                      )}
                    </Button>
                    {user?.role === 'directeur' && targetUser.id !== user.id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteUser(targetUser.id)}
                        className="h-8 px-2 text-destructive hover:text-destructive"
                        data-testid={`delete-user-${targetUser.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingUser ? t('editUser').toUpperCase() : t('newUser').toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('firstName')}</Label>
                <Input
                  value={newUser.first_name}
                  onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                  data-testid="user-firstname"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('lastName')}</Label>
                <Input
                  value={newUser.last_name}
                  onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                  data-testid="user-lastname"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('email')}</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                data-testid="user-email"
              />
            </div>

            {!editingUser && (
              <>
                <div className="space-y-2">
                  <Label>{t('password')}</Label>
                  <Input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    data-testid="user-password"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('role')}</Label>
                  <Select
                    value={newUser.role}
                    onValueChange={(v) => setNewUser({ ...newUser, role: v })}
                  >
                    <SelectTrigger data-testid="user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="collaborateur">{t('collaborator')}</SelectItem>
                      {user?.role === 'directeur' && (
                        <SelectItem value="manager">{t('manager')}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Color Picker */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Palette className="h-4 w-4" />
                {t('userColor')}
              </Label>
              <div className="flex gap-2 flex-wrap">
                {USER_COLORS.map(color => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full transition-all ${newUser.color === color ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-105'}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewUser({ ...newUser, color })}
                    type="button"
                  />
                ))}
              </div>
            </div>

            {/* Default Language */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                {t('defaultLanguage')}
              </Label>
              <Select
                value={newUser.default_language}
                onValueChange={(v) => setNewUser({ ...newUser, default_language: v })}
              >
                <SelectTrigger data-testid="user-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map(lang => (
                    <SelectItem key={lang.code} value={lang.code}>{lang.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Weekly Contractual Hours */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {t('weeklyContractualHours')}
              </Label>
              <Input
                type="number"
                min="0"
                max="60"
                step="0.5"
                value={newUser.weekly_contractual_hours}
                onChange={(e) => setNewUser({ ...newUser, weekly_contractual_hours: parseFloat(e.target.value) || 40 })}
                data-testid="user-contractual-hours"
              />
            </div>

            <Button
              onClick={editingUser ? handleUpdateUser : handleCreateUser}
              className="w-full"
              data-testid="save-user-btn"
            >
              {editingUser ? t('update') : t('createUser')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('changePassword').toUpperCase()}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {selectedUserForPassword && (
              <p className="text-sm text-muted-foreground">
                {selectedUserForPassword.first_name} {selectedUserForPassword.last_name}
              </p>
            )}
            
            <div className="space-y-2">
              <Label>{t('newPassword')}</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('newPassword')}
                data-testid="new-password-input"
              />
            </div>

            <Button
              onClick={handleResetPassword}
              className="w-full"
              data-testid="reset-password-btn"
            >
              {t('changePassword')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
